import { useFlag, useUnleashContext } from '@unleash/proxy-client-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import ChevronLeft from '../../assets/icons/ChevronLeft';
import ChevronRight from '../../assets/icons/ChevronRight';
import CuriRocketBigFlameSvg from '../../assets/icons/CuriRocketBigFlameSvg';
import CuriRocketSvg from '../../assets/icons/CuriRocketSvg';
import Logo from '../../assets/icons/Logo';
import theme from '../../assets/theme';
import { OrganizationType } from '../../hooks/useAccessBadge';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import { useMetrics } from '../../hooks/useMetrics';
import useSignIn from '../../hooks/useSignIn';
import useSignUp from '../../hooks/useSignup';
import { useMarketingMetrics } from '../../providers/PlausibleAnalyticsProvider';
import {
  GradeType,
  IntendedUseType,
  SchoolRoleType,
  SignUpOriginType,
  SubjectType,
} from '../../services/backendService/types';
import { configureStore } from '../../utils/store';
import { zIndexes } from '../../utils/zIndexes';
import { Input } from '../_atoms/_Input';
import { Button } from '../_atoms/Button';
import { IconWrapper } from '../_atoms/IconWrapper';
import OrganizationSelector, {
  SelectedLocationType,
  SelectedOrganizationType,
} from '../_molecules/OrganizationSelector';
import HotjarSnippet from '../analytics/HotJar';
import PageContentContainer from '../styled/PageContentContainer';

type CurrentForm =
  | 'role'
  | 'selectOrganization'
  | 'subjects'
  | 'grades'
  | 'intendedUses'
  | 'signUpOrigin'
  | 'referralForm'
  | 'studentJoinForm';

type ActiveFormSection = {
  title: string;
  options?: string[];
  canMultiSelect?: boolean;
  placeHolderText: string;
  label: string;
};

const SignupTopbar = () => {
  return (
    <TopbarContainer>
      <Link href="/" passHref>
        <IconWrapper color={theme.colors.white} height={'22px'} width={'81px'}>
          <Logo />
        </IconWrapper>
      </Link>
      <h3 style={{ textAlign: 'center', margin: 0, color: theme.colors.white }}>
        <Login />
      </h3>
    </TopbarContainer>
  );
};

const TopbarContainer = styled.div`
  z-index: ${zIndexes('TOPBAR')};
  width: 100%;
  height: 60px;
  flex: 0;
  padding: 10px 50px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: ${theme.colors.white};
  background-color: ${theme.colors.orange};
  position: fixed;
  top: 0;
  margin: auto;
  flex-wrap: wrap;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12);
`;

type FormSection = {
  title: string;
  options: string[];
  canMultiSelect?: boolean;
  placeHolderText: string;
  label: string;
};

type ActiveFormSectionMapper = {
  [key in Exclude<
    CurrentForm,
    'selectOrganization' | 'studentJoinForm' | 'referralForm'
  >]: FormSection;
};

const activeFormSectionMapper: ActiveFormSectionMapper = {
  role: {
    title: 'What is your primary role?',
    options: [
      'Teacher',
      'Instructional/Tech Coach',
      'Library Media Specialist',
      'School Administrator',
      'District Administrator',
      'Purchasing Manager',
      'Student',
      'Other',
    ],
    canMultiSelect: false,
    placeHolderText: 'Enter your role',
    label: 'Other role',
  },
  subjects: {
    title: 'What subject(s) do you teach?',
    options: ['English Language Arts', 'Math', 'Science', 'Social Studies', 'Other'],
    canMultiSelect: true,
    placeHolderText: 'Enter your subject(s)',
    label: 'Other subject(s)',
  },
  grades: {
    title: 'What grade(s) do you teach?',
    options: [
      'Kindergarten',
      '1st',
      '2nd',
      '3rd',
      '4th',
      '5th',
      '6th',
      '7th',
      '8th',
      '9th',
      '10th',
      '11th',
      '12th',
      '13th',
      'PD',
      'Higher Education',
      'Other',
    ],
    canMultiSelect: true,
    placeHolderText: 'Enter your grade(s)',
    label: 'Other grade(s)',
  },
  intendedUses: {
    title: 'What do you plan to use Curipod for?',
    options: [
      'Create Excitement for Reading and Writing',
      'Give Immediate Feedback on Student Writing',
      'Prepare for Testing',
      'Create Interactive Presentations Using AI',
      'Other',
    ],
    canMultiSelect: true,
    placeHolderText: 'Enter your intended use',
    label: 'Other intended use',
  },
  signUpOrigin: {
    title: 'How did you hear about Curipod?',
    options: [
      'Social Media',
      'Professional Development Session',
      'Coworker Referral',
      'Conference Presentation',
      'Other',
    ],
    canMultiSelect: false,
    placeHolderText: 'Tell us how you heard about Curipod',
    label: 'Other sign up origin',
  },
};

const RocketIcon = styled.div`
  display: inline-block;
  height: 1.5em;
  width: auto;
  margin-right: 0.3em;
  vertical-align: middle;

  svg {
    height: 100%;
    width: auto;
  }
`;

const StudentJoinLesson = () => {
  const metrics = useMetrics(true);
  const { track } = useMarketingMetrics();

  return (
    <>
      <h1 style={{ textAlign: 'center' }}>
        Jump into today&apos;s learning adventure with rocket-powered curiosity
      </h1>
      <div style={{ display: 'flex', gap: 20 }}>
        <RocketContainer>
          <CuriRocketSvg />
          <CuriRocketBigFlameSvg />
        </RocketContainer>
        <Link href={`https://${configureStore().config.game.url}`} passHref>
          <a
            style={{
              fontWeight: 500,
              fontSize: 18,
              color: theme.colors.greyLight,
              marginRight: 20,
            }}
          >
            <Button
              styles={{
                backgroundColor: theme.colors.blue,
                borderColor: theme.colors.blue,
                borderRadius: '20px',
                height: '40px',
                width: '150px',
              }}
              onClick={() => {
                track('RedirectStudentFromSignup.Curi.live.EnterPin');
                metrics.logEvent('RedirectStudentFromSignup.Curi.live.EnterPin');
              }}
            >
              Enter pin
            </Button>
          </a>
        </Link>
      </div>
    </>
  );
};

const RocketContainer = styled.div`
  position: relative;
  width: 0%;
  height: 40px;
  width: 0px;
  svg {
    height: 40px;
    width: 40px;
    position: absolute;
    right: 0px;
    top: 0px;
    transform: rotate(0deg);
    transform-origin: center;
  }

  svg:last-child {
    animation: flames 0.2s infinite linear;
  }

  @keyframes flames {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;
const RoleSelection = ({
  activeFormSection,
  setCustomRole,
  customRole,
  role,
  setRole,
  handleNext,
}: {
  activeFormSection: ActiveFormSection;
  setCustomRole: (role: string) => void;
  customRole: string | undefined;
  role: SchoolRoleType | undefined;
  setRole: (role: SchoolRoleType) => void;
  handleNext: () => void;
}) => {
  const metrics = useMetrics(true);
  const { track } = useMarketingMetrics();
  const shouldDescribeCustomOption = useMemo(() => {
    return role === 'Other';
  }, [role]);

  const handleSelect = (option: SchoolRoleType) => {
    track('Signup.Role.Option.Selected', {
      props: {
        selectedOption: option,
      },
    });
    metrics.logEvent('Signup.Role.Option.Selected', {
      selectedOption: option,
    });
    setRole(option);
  };

  return (
    <>
      <h1>{activeFormSection.title}</h1>
      <OptionsContainer>
        {activeFormSection.options &&
          activeFormSection.options.map((option, i) => (
            <StyledOption
              $isSelected={role === option}
              key={i}
              onClick={() => handleSelect(option as SchoolRoleType)}
            >
              {option}
            </StyledOption>
          ))}
      </OptionsContainer>
      {shouldDescribeCustomOption && (
        <div>
          <p style={{ textAlign: 'left', width: '100%', marginBottom: 0 }}>
            {activeFormSection.label}
          </p>
          <Input
            maxLength={50}
            style={{ width: 250, fontSize: 16 }}
            placeholder={activeFormSection.placeHolderText}
            onChange={e => setCustomRole(e.target.value)}
            value={customRole}
          />
        </div>
      )}
      <Button
        disabled={role === undefined}
        onClick={() => {
          handleNext();
        }}
      >
        Next
      </Button>
    </>
  );
};

const SubjectSelection = ({
  activeFormSection,
  setCustomSubjects,
  customSubjects,
  subjects,
  setSubjects,
  handleNext,
}: {
  activeFormSection: ActiveFormSection;
  setCustomSubjects: (subjects: string) => void;
  customSubjects: string | undefined;
  subjects: SubjectType[] | undefined;
  setSubjects: (subjects: SubjectType[] | undefined) => void;
  handleNext: () => void;
}) => {
  const metrics = useMetrics(true);
  const { track } = useMarketingMetrics();

  const shouldDescribeCustomOption = useMemo(() => {
    return subjects?.includes('Other');
  }, [subjects]);

  const handleSelect = (option: SubjectType) => {
    track('Signup.Subject.Option.Selected', {
      props: {
        selectedOption: option,
      },
    });
    metrics.logEvent('Signup.Subject.Option.Selected', {
      selectedOption: option,
    });

    if (subjects?.includes(option)) {
      setSubjects(
        subjects.filter(s => s !== option).length === 0
          ? undefined
          : (subjects.filter(s => s !== option) as SubjectType[]),
      );
      return;
    }
    if (subjects === undefined) {
      setSubjects([option] as SubjectType[]);
    } else {
      setSubjects([...subjects, option] as SubjectType[]);
    }
  };

  return (
    <>
      <h1>{activeFormSection.title}</h1>
      <OptionsContainer>
        {activeFormSection.options &&
          activeFormSection.options.map((option, i) => (
            <StyledOption
              $isSelected={subjects?.includes(option as SubjectType) || false}
              key={i}
              onClick={() => handleSelect(option as SubjectType)}
            >
              {option}
            </StyledOption>
          ))}
      </OptionsContainer>
      {shouldDescribeCustomOption && (
        <div>
          <p style={{ textAlign: 'left', width: '100%', marginBottom: 0 }}>
            {activeFormSection.label}
          </p>
          <Input
            maxLength={50}
            style={{ width: 250, fontSize: 16 }}
            placeholder={activeFormSection.placeHolderText}
            onChange={e => setCustomSubjects(e.target.value)}
            value={customSubjects}
          />
        </div>
      )}
      <Button
        onClick={() => {
          handleNext();
        }}
      >
        Next
      </Button>
    </>
  );
};

const GradeSelection = ({
  activeFormSection,
  setCustomGrades,
  customGrades,
  grades,
  setGrades,
  handleNext,
}: {
  activeFormSection: ActiveFormSection;
  setCustomGrades: (grades: string) => void;
  customGrades: string | undefined;
  grades: GradeType[] | undefined;
  setGrades: (grades: GradeType[] | undefined) => void;
  handleNext: () => void;
}) => {
  const metrics = useMetrics(true);
  const { track } = useMarketingMetrics();
  const shouldDescribeCustomOption = useMemo(() => {
    return grades?.includes('Other');
  }, [grades]);

  const handleSelect = (option: GradeType) => {
    track('Signup.Grade.Option.Selected', {
      props: {
        selectedOption: option,
      },
    });
    metrics.logEvent('Signup.Grade.Option.Selected', {
      selectedOption: option,
    });

    if (grades?.includes(option)) {
      setGrades(
        grades.filter(s => s !== option).length === 0
          ? undefined
          : grades.filter(s => s !== option),
      );
      return;
    }
    if (grades === undefined) {
      setGrades([option]);
    } else {
      setGrades([...grades, option]);
    }
  };

  return (
    <>
      <h1>{activeFormSection.title}</h1>
      <OptionsContainer>
        {activeFormSection.options &&
          activeFormSection.options.map((option, i) => (
            <StyledOption
              $isSelected={grades?.includes(option as GradeType) || false}
              key={i}
              onClick={() => handleSelect(option as GradeType)}
            >
              {option}
            </StyledOption>
          ))}
      </OptionsContainer>
      {shouldDescribeCustomOption && (
        <div>
          <p style={{ textAlign: 'left', width: '100%', marginBottom: 0 }}>
            {activeFormSection.label}
          </p>
          <Input
            maxLength={50}
            style={{ width: 250, fontSize: 16 }}
            placeholder={activeFormSection.placeHolderText}
            onChange={e => setCustomGrades(e.target.value)}
            value={customGrades}
          />
        </div>
      )}
      <Button
        onClick={() => {
          handleNext();
        }}
      >
        Next
      </Button>
    </>
  );
};

export const SelectWrapper = styled.div<{ $fluid?: boolean }>`
  cursor: pointer;
  .ant-select {
    width: 100%;
  }
  &&& .ant-select .ant-select-selector {
    border: 1px solid #d9d9d9;
    border-radius: 5px;
    height: 42px;
    display: flex;
    align-items: center;
    font-size: 16px;
    ${props =>
      props.$fluid &&
      css`
        width: 100%;
      `}
  }
  .ant-select-selection-item {
    height: 100%;
    align-items: center;
    display: flex;
  }
  .ant-select-selection-placeholder {
    color: ${theme.colors.black};
    font-weight: normal;
  }
  ${props =>
    props.$fluid &&
    css`
      height: 100%;
    `}
`;

const IntendedUseSelection = ({
  activeFormSection = activeFormSectionMapper['intendedUses'],
  setCustomIntendedUse,
  customIntendedUse,
  intendedUses,
  setIntendedUse,
  handleNext,
}: {
  activeFormSection?: ActiveFormSection;
  setCustomIntendedUse: (intendedUses: string) => void;
  customIntendedUse: string | undefined;
  intendedUses: IntendedUseType[] | undefined;
  setIntendedUse: (intendedUses: IntendedUseType[] | undefined) => void;
  handleNext: () => void;
}) => {
  const metrics = useMetrics(true);
  const { track } = useMarketingMetrics();
  const shouldDescribeCustomOption = useMemo(() => {
    return intendedUses?.includes('Other');
  }, [intendedUses]);

  const handleSelect = (option: IntendedUseType) => {
    track('Signup.IntendedUse.Option.Selected', {
      props: {
        selectedOption: option,
      },
    });
    metrics.logEvent('Signup.IntendedUse.Option.Selected', {
      selectedOption: option,
    });

    if (intendedUses?.includes(option)) {
      setIntendedUse(
        intendedUses.filter(s => s !== option).length === 0
          ? undefined
          : intendedUses.filter(s => s !== option),
      );
      return;
    }
    if (intendedUses === undefined) {
      setIntendedUse([option]);
    } else {
      setIntendedUse([...intendedUses, option]);
    }
  };

  return (
    <>
      <h1>{activeFormSection.title}</h1>
      <OptionsContainer>
        {activeFormSection.options &&
          activeFormSection.options.map((option, i) => (
            <StyledOption
              $isSelected={intendedUses?.includes(option as IntendedUseType) || false}
              key={i}
              onClick={() => handleSelect(option as IntendedUseType)}
            >
              {option}
            </StyledOption>
          ))}
      </OptionsContainer>
      {shouldDescribeCustomOption && (
        <div>
          <p style={{ textAlign: 'left', width: '100%', marginBottom: 0 }}>
            {activeFormSection.label}
          </p>
          <Input
            maxLength={50}
            style={{ width: 250, fontSize: 16 }}
            placeholder={activeFormSection.placeHolderText}
            onChange={e => setCustomIntendedUse(e.target.value)}
            value={customIntendedUse}
          />
        </div>
      )}
      <Button onClick={handleNext}>Next</Button>
    </>
  );
};

const SignUpOriginSelection = ({
  activeFormSection = activeFormSectionMapper['signUpOrigin'],
  setCustomSignUpOrigin,
  customSignUpOrigin,
  signUpOrigin,
  setSignUpOrigin,
  handleNext,
}: {
  activeFormSection?: ActiveFormSection;
  setCustomSignUpOrigin: (signUpOrigin: string) => void;
  customSignUpOrigin: string | undefined;
  signUpOrigin: SignUpOriginType | undefined;
  setSignUpOrigin: (signUpOrigin: SignUpOriginType | undefined) => void;
  handleNext: () => void;
}) => {
  const metrics = useMetrics(true);
  const { track } = useMarketingMetrics();
  const shouldDescribeCustomOption = useMemo(() => {
    return signUpOrigin === 'Other';
  }, [signUpOrigin]);

  const handleSelect = (option: SignUpOriginType) => {
    track('Signup.SignUpOrigin.Option.Selected', {
      props: {
        selectedOption: option,
      },
    });
    metrics.logEvent('Signup.SignUpOrigin.Option.Selected', {
      selectedOption: option,
    });
    setSignUpOrigin(option);
  };

  return (
    <>
      <h1>{activeFormSection.title}</h1>
      <OptionsContainer>
        {activeFormSection.options &&
          activeFormSection.options.map((option, i) => (
            <StyledOption
              $isSelected={signUpOrigin === option}
              key={i}
              onClick={() => handleSelect(option as SignUpOriginType)}
            >
              {option}
            </StyledOption>
          ))}
      </OptionsContainer>
      {shouldDescribeCustomOption && (
        <div>
          <p style={{ textAlign: 'left', width: '100%', marginBottom: 0 }}>
            {activeFormSection.label}
          </p>
          <Input
            maxLength={50}
            style={{ width: 250, fontSize: 16 }}
            placeholder={activeFormSection.placeHolderText}
            onChange={e => setCustomSignUpOrigin(e.target.value)}
            value={customSignUpOrigin}
          />
        </div>
      )}
      <Button onClick={handleNext}>Next</Button>
    </>
  );
};

const ReferralForm = ({ handleNext }: { handleNext: () => void }) => {
  const metrics = useMetrics(true);
  const { track } = useMarketingMetrics();
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [subject, setSubject] = useState('I found an awesome AI tool! Try it with me?');
  const [message, setMessage] = useState(
    'Hi, I found this awesome AI tool for creating lessons where students look up from their screens and talk. I would love to try it out with you. \nYou can sign up here: https://curipod.com/signup',
  );
  const [showThankYou, setShowThankYou] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [invalidEmailWarning, setInvalidEmailWarning] = useState(false);

  useEffect(() => {
    if (duplicateWarning) {
      const timer = setTimeout(() => {
        setDuplicateWarning(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [duplicateWarning]);

  useEffect(() => {
    if (invalidEmailWarning) {
      const timer = setTimeout(() => {
        setInvalidEmailWarning(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [invalidEmailWarning]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(e.target.value);
    // Clear duplicate warning when user starts typing
    if (duplicateWarning) {
      setDuplicateWarning(false);
    }
    // Clear invalid email warning when user starts typing
    if (invalidEmailWarning) {
      setInvalidEmailWarning(false);
    }
  };

  const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addEmail();
    } else if (e.key === 'Backspace' && emailInput === '' && emails.length > 0) {
      removeEmail(emails.length - 1);
    }
  };

  const handleEmailInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const pastedEmails = pastedText.split(/[,;\s]+/).filter(email => email.trim() !== '');

    const validEmails = pastedEmails.filter(email => validateEmail(email.trim()));
    // Check for duplicates before filtering
    const duplicatesFound = validEmails.some(email => emails.includes(email.trim()));
    if (duplicatesFound) {
      setDuplicateWarning(true);
    }
    // Filter out duplicates by checking against existing emails
    const uniqueEmails = validEmails.filter(email => !emails.includes(email.trim()));
    const newEmails = [...emails, ...uniqueEmails].slice(0, 20); // Limit to 20 emails
    setEmails(newEmails);
    setEmailInput('');
  };

  const addEmail = () => {
    const email = emailInput.trim();
    if (email && emails.length < 20) {
      if (!validateEmail(email)) {
        setInvalidEmailWarning(true);
        return;
      }
      if (emails.includes(email)) {
        setDuplicateWarning(true);
        return;
      }
      setEmails([...emails, email]);
      setEmailInput('');
    }
  };

  const removeEmail = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const handleSendInvites = () => {
    // Track the referral event with all details
    track('Signup.Referral.Referer', {
      props: {
        emailCount: emails.length,
        referredEmails: emails, // Include actual email addresses
        subject,
        message,
      },
    });
    metrics.logEvent('Signup.Referral.Referer', {
      emailCount: emails.length,
      referredEmails: emails, // Include actual email addresses
      subject,
      message,
    });

    // Track individual referral events for each email to make Mixpanel exports easier
    emails.forEach((email, index) => {
      track('Signup.Referral.Referent', {
        props: {
          referredEmail: email,
          referralPosition: index + 1,
          totalReferrals: emails.length,
          subject,
          message,
        },
      });
      metrics.logEvent('Signup.Referral.Referent', {
        referredEmail: email,
        referralPosition: index + 1,
        totalReferrals: emails.length,
        subject,
        message,
      });
    });

    setShowThankYou(true);
    handleNext();
  };

  if (showThankYou) {
    return (
      <ThankYouContainer>
        <h1>Thank you!</h1>
        <h3>We will confirm when we have enabled the reward in a couple of days!</h3>
        <Button>Next</Button>
      </ThankYouContainer>
    );
  }

  return (
    <ReferralFormWrapper>
      <ReferralFormContent>
        <LeftColumn>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <ReferralHeaderText>
              <RocketIcon>
                <CuriRocketSvg />
              </RocketIcon>
              Invite 5+ educator friends to use Curipod and get access to{' '}
              <strong>standards alignment & test prep for 30 days</strong>, as a thank
              you!
            </ReferralHeaderText>
          </div>

          <div>
            <ButtonGroupWrapper>
              <ButtonContainer>
                <Button
                  disabled={emails.length < 1}
                  onClick={handleSendInvites}
                  styles={{ flex: 1 }}
                >
                  Send Invites ({emails.length})
                </Button>
                <Button
                  type="ghost"
                  onClick={() => handleNext()}
                  styles={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: theme.colors.black,
                    boxShadow: 'none',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  Skip
                  <ChevronRight color={theme.colors.black} />
                </Button>
              </ButtonContainer>
              <DisclaimerText>
                *We hate spam too! Only this invitation will be sent to the emails you
                provide.
              </DisclaimerText>
            </ButtonGroupWrapper>
          </div>
        </LeftColumn>

        <RightColumn>
          <EmailInputContainer>
            <label htmlFor="from">From</label>
            <FromFieldContainer>
              <strong>via Curipod</strong> &lt;share@curipod.com&gt;
            </FromFieldContainer>
          </EmailInputContainer>

          <EmailInputContainer>
            <label htmlFor="emails">To</label>
            <EmailChipsContainer $hasWarning={duplicateWarning}>
              {emails.map((email, index) => (
                <EmailChip key={index}>
                  {email}
                  <EmailChipRemove onClick={() => removeEmail(index)}>×</EmailChipRemove>
                </EmailChip>
              ))}
              <EmailInput
                id="emails"
                value={emailInput}
                onChange={handleEmailInputChange}
                onKeyDown={handleEmailInputKeyDown}
                onPaste={handleEmailInputPaste}
                placeholder={emails.length === 0 ? 'Enter email addresses...' : ''}
                autoComplete="off"
                autoFocus
              />
              {duplicateWarning && (
                <DuplicateWarning>This email has already been added</DuplicateWarning>
              )}
              {invalidEmailWarning && (
                <InvalidEmailWarning>
                  Please enter a valid email address
                </InvalidEmailWarning>
              )}
            </EmailChipsContainer>
            <HelperText>
              Separate multiple emails with commas or spaces. You can also paste multiple
              comma-separated emails at once.
            </HelperText>
          </EmailInputContainer>

          <InputContainer>
            <label htmlFor="subject">Subject</label>
            <Input
              id="subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              style={{ width: '100%', fontSize: 16 }}
            />
          </InputContainer>

          <InputContainer>
            <label htmlFor="message">Message</label>
            <MessageTextarea
              id="message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={4}
            />
          </InputContainer>
        </RightColumn>
      </ReferralFormContent>

      <MobileButtonContainer>
        <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
          <Button
            disabled={emails.length < 1}
            onClick={handleSendInvites}
            styles={{ flex: 1 }}
          >
            Send Invites ({emails.length})
          </Button>
          <Button
            type="ghost"
            onClick={() => handleNext()}
            styles={{
              backgroundColor: 'transparent',
              border: 'none',
              color: theme.colors.black,
              boxShadow: 'none',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            Skip
            <ChevronRight color={theme.colors.black} />
          </Button>
        </div>
        <DisclaimerText style={{ marginTop: '8px', textAlign: 'center' }}>
          *We hate spam too! Only this invitation will be sent to the emails you provide.
        </DisclaimerText>
      </MobileButtonContainer>
    </ReferralFormWrapper>
  );
};

const ThankYouContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;

  h1 {
    color: ${theme.colors.black};
    margin-bottom: 20px;
  }

  p {
    color: ${theme.colors.black};
  }
`;

const ReferralFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ReferralFormContent = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 1000px;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const LeftColumn = styled.div`
  flex: 1.5;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px;

  @media (max-width: 768px) {
    order: 1;
    justify-content: flex-start;
  }
`;

const RightColumn = styled.div`
  flex: 1.5;
  display: flex;
  flex-direction: column;
  padding: 20px;

  @media (max-width: 768px) {
    order: 2;
    padding: 20px 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  width: 100%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const EmailInputContainer = styled.div`
  width: 100%;
  margin-bottom: 20px;
  padding-left: 10px;
  padding-right: 10px;
  position: relative;

  label {
    display: block;
    margin-bottom: 0px;
    font-weight: 600;
    text-align: left;
  }
`;

const EmailChipsContainer = styled.div<{ $hasWarning: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  min-height: 42px;
  align-items: center;
  background-color: white;

  &:focus-within {
    border-color: ${theme.colors.orange};
    box-shadow: 0 0 0 2px rgba(255, 165, 0, 0.2);
  }

  ${({ $hasWarning }) =>
    $hasWarning &&
    css`
      border-color: ${theme.colors.red};
      box-shadow: 0 0 0 2px rgba(255, 0, 0, 0.2);
    `}
`;

const EmailChip = styled.span`
  display: inline-flex;
  align-items: center;
  background-color: ${theme.colors.orange};
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 14px;
  gap: 4px;
`;

const EmailChipRemove = styled.span`
  cursor: pointer;
  font-weight: bold;
  font-size: 16px;
  line-height: 1;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const EmailInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 120px;
  font-size: 16px;
  padding: 4px;

  &::placeholder {
    color: #999;
  }
`;

const DuplicateWarning = styled.div`
  position: absolute;
  top: -17px;
  right: 0px;
  background-color: ${theme.colors.red || '#ff4d4f'};
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    right: 12px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid ${theme.colors.red || '#ff4d4f'};
  }
`;

const InputContainer = styled.div`
  width: 100%;
  margin-bottom: 20px;
  padding-left: 10px;
  padding-right: 10px;
  position: relative;

  label {
    display: block;
    margin-bottom: 0px;
    font-weight: 600;
    text-align: left;
  }
`;

const MessageTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    border-color: ${theme.colors.blue};
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    outline: none;
  }
`;

const HelperText = styled.span`
  font-size: 12px;
  color: ${theme.colors.black};
  margin-top: 4px;
  display: block;
`;

const FromFieldContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  background-color: #f5f5f5;
  color: ${theme.colors.black};
  font-size: 16px;
  min-height: 42px;
  box-sizing: border-box;
  overflow: hidden;
`;

const MobileButtonContainer = styled.div`
  display: none;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  padding-left: 10px;
  padding-right: 10px;

  @media (max-width: 768px) {
    display: flex;
    order: 3;
    align-items: center;
  }
`;

const ButtonGroupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DisclaimerText = styled.span`
  font-size: 12px;
  color: ${theme.colors.black};
  margin-top: 8px;
  display: block;
  line-height: 1.4;
`;

const ReferralHeaderText = styled.p`
  font-weight: normal;
  line-height: 1.3;
  font-size: 28px;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 1.2;
    text-align: center;
  }
`;

const CreateAccountForm = ({
  onOrganizationSelect,
}: {
  onOrganizationSelect: (org: SelectedOrganizationType | null) => void;
}) => {
  useUnleashContext();

  const districtSignupEnabled = useFlag('orgPickerV2districtSignupEnabled');
  const referralStepEnabled = useFlag('signupReferralStepEnabled');

  const metrics = useMetrics(true);
  const { track } = useMarketingMetrics();
  const [role, setRole] = useState<SchoolRoleType>();
  const [customRole, setCustomRole] = useState<string>();
  const [subjects, setSubjects] = useState<SubjectType[] | undefined>();
  const [intendedUses, setIntendedUse] = useState<IntendedUseType[] | undefined>();
  const [signUpOrigin, setSignUpOrigin] = useState<SignUpOriginType>();
  const [grades, setGrades] = useState<GradeType[] | undefined>();
  const [customSubjects, setCustomSubjects] = useState<string>();
  const [customGrades, setCustomGrades] = useState<string>();
  const [currentForm, setCurrentForm] = useState<CurrentForm>('role');
  const [customIntendedUse, setCustomIntendedUse] = useState<string>();
  const [customSignUpOrigin, setCustomSignUpOrigin] = useState<string>();

  // Noting there are inconsistencies between here and organization selector
  // about the use of null or undefined, you'll see both and it's caused by
  // requirements from 3rd party components.  Refactoring to use undefined
  // is probably warranted but low priority.
  const [
    selectedOrganization,
    setSelectedOrganization,
  ] = useState<SelectedOrganizationType | null>(null);
  const [organizationType, setOrganizationType] = useState<OrganizationType | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocationType | null>(
    null,
  );
  const [hideOrganizationTypePicker, setHideOrganizationTypePicker] = useState<boolean>(
    false,
  );
  const [customOrganizationName, setCustomOrganizationName] = useState<string>();
  const [needsCustomOrganizationName, setNeedsCustomOrganizationName] = useState<boolean>(
    false,
  );

  const [locationFetchError, setLocationFetchError] = useState<string | undefined>();

  const handleChangeOrgType = useCallback(
    (newOrgType: OrganizationType | null) => {
      if (organizationType && organizationType !== newOrgType) {
        setSelectedOrganization(null);
        setCustomOrganizationName(undefined);
        setNeedsCustomOrganizationName(false);
      }
      setOrganizationType(newOrgType);
    },
    [organizationType, setNeedsCustomOrganizationName],
  );

  const handleChangeLocation = (location: SelectedLocationType | null) => {
    if (
      selectedLocation?.placeAutocomplete?.place_id !==
      location?.placeAutocomplete?.place_id
    ) {
      setSelectedOrganization(null);
      setCustomOrganizationName(undefined);
      setNeedsCustomOrganizationName(false);
    }
    setSelectedLocation(location);
  };

  const handleChangeOrganization = (organization: SelectedOrganizationType | null) => {
    setSelectedOrganization(organization);
    onOrganizationSelect(organization);
  };

  //determine whether the current form is the last form before submitting
  const isFinalForm = useMemo(() => {
    if (role === 'Student') {
      return currentForm === 'studentJoinForm';
    } else {
      const isUSOrganization = selectedOrganization?.organization.country === 'US';
      if (referralStepEnabled && isUSOrganization) {
        return currentForm === 'referralForm';
      } else {
        return currentForm === 'signUpOrigin';
      }
    }
  }, [
    currentForm,
    role,
    referralStepEnabled,
    selectedOrganization?.organization.country,
  ]);

  const selectNextCurrentForm = useCallback(() => {
    switch (currentForm) {
      case 'role':
        if (role === 'Student') {
          setCurrentForm('studentJoinForm');
          break;
        }

        setCurrentForm('selectOrganization');
        if (!customOrganizationName) {
          setLocationFetchError(undefined);
          setNeedsCustomOrganizationName(false);
        }
        switch (role) {
          case 'Teacher':
          case 'School Administrator':
            handleChangeOrgType(OrganizationType.School);
            setHideOrganizationTypePicker(true);
            break;
          case 'District Administrator':
            handleChangeOrgType(OrganizationType.District);
            setHideOrganizationTypePicker(true);
            break;
          default:
            handleChangeOrgType(null);
            setHideOrganizationTypePicker(false);
            break;
        }
        break;
      case 'selectOrganization':
        if (role === 'Teacher') {
          setCurrentForm('subjects');
        } else {
          setCurrentForm('signUpOrigin');
        }
        break;
      case 'subjects':
        setCurrentForm('grades');
        break;
      case 'grades':
        setCurrentForm('intendedUses');
        break;
      case 'intendedUses':
        setCurrentForm('signUpOrigin');
        break;
      case 'signUpOrigin':
        const isUSOrganization = selectedOrganization?.organization.country === 'US';
        if (referralStepEnabled && isUSOrganization) {
          setCurrentForm('referralForm');
        }
        break;
      // referralForm is now the last form (when enabled)
      default:
        break;
    }
  }, [
    currentForm,
    customOrganizationName,
    handleChangeOrgType,
    role,
    referralStepEnabled,
    selectedOrganization?.organization.country,
  ]);

  const selectPreviousForm = useCallback(
    (currentForm: CurrentForm) => {
      switch (currentForm) {
        case 'studentJoinForm':
          setCurrentForm('role');
          break;
        case 'selectOrganization':
          setCurrentForm('role');
          break;
        case 'subjects':
          setSubjects(undefined);
          setCustomSubjects(undefined);
          setCurrentForm('selectOrganization');
          break;
        case 'grades':
          setGrades(undefined);
          setCustomGrades(undefined);
          setCurrentForm('subjects');
          break;
        case 'intendedUses':
          setIntendedUse(undefined);
          setCustomIntendedUse(undefined);
          setCurrentForm('grades');
          break;
        case 'signUpOrigin':
          if (role === 'Teacher') {
            setCurrentForm('intendedUses');
          } else {
            setCurrentForm('selectOrganization');
          }
          break;
        case 'referralForm':
          setCurrentForm('signUpOrigin');
          break;
        default:
          break;
      }
    },
    [role],
  );

  const { signUp } = useSignUp();
  const router = useRouter();
  const callback = router.query.callback as string | undefined;
  const handleGoBack = () => {
    selectPreviousForm(currentForm);
    track('Signup.Back.Button.Clicked', {
      props: {
        currentSignupPage: currentForm,
        from: 'signup',
      },
    });
    metrics.logEvent('Signup.Back.Button.Clicked', {
      currentSignupPage: currentForm,
      from: 'signup',
    });
  };
  const canSkipCurrentForm = useMemo(() => {
    return (
      currentForm === 'grades' ||
      currentForm === 'subjects' ||
      currentForm === 'intendedUses' ||
      currentForm === 'signUpOrigin' ||
      (currentForm === 'referralForm' && referralStepEnabled)
    );
  }, [currentForm, referralStepEnabled]);

  const handleGoForward = (isSkip?: boolean) => {
    // Check if the current form is the last form before submitting
    if (isFinalForm) {
      handleSubmit();
      return;
    }

    if (isSkip) {
      track(`Signup.Skip.Button.Clicked`, {
        props: {
          currentSignupPage: currentForm,
        },
      });
      metrics.logEvent('Signup.Skip.Button.Clicked', {
        currentSignupPage: currentForm,
      });
    } else {
      track(`Signup.Next.Button.Clicked`, {
        props: {
          currentSignupPage: currentForm,
          schoolRole: role ? role : '',
          subjects: subjects ? JSON.stringify(subjects) : '',
          grades: grades ? JSON.stringify(grades) : '',
          otherRole: customRole ? customRole : '',
          otherSubjects: customSubjects ? customSubjects : '',
          otherGrades: customGrades ? customGrades : '',
          organization: selectedOrganization?.organization
            ? selectedOrganization?.organization
            : '',
          intendedUses: intendedUses ? JSON.stringify(intendedUses) : '',
          signUpOrigin: signUpOrigin ? JSON.stringify(signUpOrigin) : '',
          otherIntendedUse: customIntendedUse || '',
          otherSignUpOrigin: customSignUpOrigin || '',
        },
      });
      metrics.logEvent('Signup.Next.Button.Clicked', {
        currentSignupPage: currentForm,
        schoolRole: role ? role : '',
        subjects: subjects ? JSON.stringify(subjects) : '',
        grades: grades ? JSON.stringify(grades) : '',
        otherRole: customRole ? customRole : '',
        otherSubjects: customSubjects ? customSubjects : '',
        otherGrades: customGrades ? customGrades : '',
        organization: selectedOrganization?.organization
          ? selectedOrganization?.organization
          : '',
        intendedUses: intendedUses ? JSON.stringify(intendedUses) : '',
        signUpOrigin: signUpOrigin ? JSON.stringify(signUpOrigin) : '',
        otherIntendedUse: customIntendedUse || '',
        otherSignUpOrigin: customSignUpOrigin || '',
      });
    }

    selectNextCurrentForm();
  };

  const allOptionsSelected = useMemo(() => {
    switch (currentForm) {
      case 'role':
        return role !== undefined;
      case 'subjects':
        if (!subjects) return false;
        return (
          subjects.length >= activeFormSectionMapper[currentForm]?.options.length - 1
        );
      case 'grades':
        if (!grades) return false;
        return grades.length >= activeFormSectionMapper[currentForm]?.options.length - 1;
      case 'intendedUses':
        return intendedUses !== undefined;
      case 'signUpOrigin':
        return signUpOrigin !== undefined;
      default:
        return false;
    }
  }, [currentForm, role, subjects, grades, intendedUses, signUpOrigin]);

  const handleSelectAll = useCallback(() => {
    switch (currentForm) {
      case 'subjects':
        setSubjects(
          activeFormSectionMapper[currentForm]?.options.filter(
            o => o !== 'Other',
          ) as SubjectType[],
        );
        break;
      case 'grades':
        setGrades(
          activeFormSectionMapper[currentForm]?.options.filter(
            o => o !== 'Other',
          ) as GradeType[],
        );
        break;
      default:
        break;
    }
    track(`Signup.SelectAll`, {
      props: {
        currentSignupPage: currentForm,
      },
    });
    metrics.logEvent('Signup.SelectAll', {
      currentSignupPage: currentForm,
    });
  }, [currentForm, track, metrics]);

  const handleDeselectAll = useCallback(() => {
    switch (currentForm) {
      case 'subjects':
        setSubjects(undefined);
        break;
      case 'grades':
        setGrades(undefined);
        break;
      case 'intendedUses':
        setIntendedUse(undefined);
        break;
      case 'signUpOrigin':
        setSignUpOrigin(undefined);
        break;
      default:
        break;
    }
    track(`Signup.DeselectAll`, {
      props: {
        currentSignupPage: currentForm,
      },
    });
    metrics.logEvent('Signup.DeselectAll', {
      currentSignupPage: currentForm,
    });
  }, [currentForm, track, metrics]);

  const userInfo = {
    role: role,
    subjects: JSON.stringify(subjects),
    grades: JSON.stringify(grades),
    otherRole: customRole,
    otherSubjects: customSubjects,
    otherGrades: customGrades,
    organization: selectedOrganization,
    intendedUses: intendedUses,
    signUpOrigin: signUpOrigin,
  };

  const handleSubmit = () => {
    metrics.logEvent('SignUp.Submitted', {
      schoolRole: userInfo.role,
      subjects: userInfo.subjects,
      grades: userInfo.grades,
      otherRole: userInfo.otherRole,
      otherSubjects: userInfo.otherSubjects,
      otherGrades: userInfo.otherGrades,
      organization: userInfo.organization,
      intendedUses: userInfo.intendedUses,
      signUpOrigin: userInfo.signUpOrigin,
      otherIntendedUse: customIntendedUse || '',
      otherSignUpOrigin: customSignUpOrigin || '',
    });
    track('SignUp.Submitted', {
      props: {
        event: 'submit',
        from: 'signup',
        schoolRole: userInfo.role,
        subjects: userInfo.subjects,
        grades: userInfo.grades,
        otherRole: userInfo.otherRole,
        otherSubjects: userInfo.otherSubjects,
        otherGrades: userInfo.otherGrades,
        organization: userInfo.organization,
        intendedUses: userInfo.intendedUses ? JSON.stringify(userInfo.intendedUses) : '',
        signUpOrigin: userInfo.signUpOrigin ? JSON.stringify(userInfo.signUpOrigin) : '',
        otherIntendedUse: customIntendedUse || '',
        otherSignUpOrigin: customSignUpOrigin || '',
      },
    });

    const finalCallbackUrl = callback ? decodeURIComponent(callback) : '/lessons';

    signUp({
      callbackUrl: finalCallbackUrl,
      authParams: {
        accountType: 'teacher',
        ui_locales: 'en',
        tenantName: selectedOrganization?.organization.name
          ? selectedOrganization.organization.name
          : '',
        schoolRole: userInfo.role ? userInfo.role : '',
        subjects: userInfo.subjects ? JSON.stringify(userInfo.subjects) : '',
        grades: userInfo.grades ? JSON.stringify(userInfo.grades) : '',
        otherRole: userInfo.otherRole ? userInfo.otherRole : '',
        otherSubjects: userInfo.otherSubjects ? userInfo.otherSubjects : '',
        otherGrades: userInfo.otherGrades ? userInfo.otherGrades : '',
        v3Properties: JSON.stringify({
          organizationId: !selectedOrganization?.organization.custom
            ? selectedOrganization?.organization.id
            : undefined,
          organizationGeoLocation: {
            country: selectedOrganization?.organization.country,
            region: selectedOrganization?.organization.region,
            city: selectedOrganization?.organization.city,
          },
          intendedUses: userInfo.intendedUses
            ? JSON.stringify(userInfo.intendedUses)
            : '',
          signUpOrigin: userInfo.signUpOrigin ? userInfo.signUpOrigin : '',
          otherIntendedUse: customIntendedUse || '',
          otherSignUpOrigin: customSignUpOrigin || '',
        }),
      },
    });
  };

  const formNumber = useMemo(() => {
    switch (currentForm) {
      case 'role':
        return 0;
      case 'selectOrganization':
        return 1;
      case 'subjects':
        return 2;
      case 'grades':
        return 3;
      case 'intendedUses':
        return 4;
      case 'signUpOrigin':
        return 5;
      case 'referralForm':
        return referralStepEnabled ? 6 : 5;
      default:
        return 0;
    }
  }, [currentForm, referralStepEnabled]);

  const doesNeedCustomOrganizationName = () => {
    if (!districtSignupEnabled && organizationType === OrganizationType.District) {
      return true;
    }

    return needsCustomOrganizationName || !!customOrganizationName;
  };

  // This is temporary and can be removed when we remove the districtSignupEnabled toggle
  const shouldAllowSearchRetry = () => {
    if (!districtSignupEnabled && organizationType === OrganizationType.District) {
      return false;
    }
    return true;
  };

  const shouldShowNextButtonAfterOrgSelection = () => {
    if (selectedOrganization) {
      return true;
    }

    if (needsCustomOrganizationName && customOrganizationName) {
      return true;
    }

    const isInternationalOrIncomplete =
      selectedLocation?.place?.country && selectedLocation?.place?.country !== 'US';
    if (isInternationalOrIncomplete && customOrganizationName) {
      return true;
    }

    return false;
  };

  return (
    <>
      <InfoBar
        handleGoBack={handleGoBack}
        showProgress={role === 'Teacher' && currentForm !== 'role'}
        canGoBack={currentForm !== 'role'}
        canSkipCurrentForm={canSkipCurrentForm}
        handleGoForward={handleGoForward}
        formNumber={formNumber}
      />

      <SignupFlowContainer>
        {currentForm === 'role' && (
          <RoleSelection
            activeFormSection={activeFormSectionMapper[currentForm]}
            customRole={customRole}
            setCustomRole={setCustomRole}
            role={role}
            setRole={setRole}
            handleNext={handleGoForward}
          />
        )}
        {currentForm === 'selectOrganization' && (
          <>
            {hideOrganizationTypePicker && organizationType ? (
              <h1>Select your {organizationType}</h1>
            ) : (
              <h1>Select your School/District</h1>
            )}
            <OrganizationSelector
              organizationType={organizationType}
              onSelectOrganizationType={handleChangeOrgType}
              onSelectLocation={handleChangeLocation}
              selectedLocation={selectedLocation}
              onSelectOrganization={handleChangeOrganization}
              selectedOrganization={selectedOrganization}
              hideOrganizationTypePicker={hideOrganizationTypePicker}
              customOrganizationName={customOrganizationName}
              onSetCustomOrganizationName={setCustomOrganizationName}
              locationFetchError={locationFetchError}
              onLocationFetchError={setLocationFetchError}
              needsCustomOrganizationName={doesNeedCustomOrganizationName()}
              onSetNeedsCustomOrganizationName={setNeedsCustomOrganizationName}
              // This is temporary and can be removed when we remove the districtSignupEnabled toggle
              disableSearchRetry={!shouldAllowSearchRetry()}
              fluid
              metricEventNamePrefix="Signup"
            />

            {shouldShowNextButtonAfterOrgSelection() && (
              <Button onClick={() => handleGoForward()} styles={{ marginTop: 20 }}>
                Next
              </Button>
            )}
          </>
        )}
        {currentForm === 'subjects' && (
          <SubjectSelection
            activeFormSection={activeFormSectionMapper[currentForm]}
            customSubjects={customSubjects}
            setCustomSubjects={setCustomSubjects}
            subjects={subjects}
            setSubjects={setSubjects}
            handleNext={handleGoForward}
          />
        )}
        {currentForm === 'grades' && (
          <GradeSelection
            activeFormSection={activeFormSectionMapper[currentForm]}
            customGrades={customGrades}
            setCustomGrades={setCustomGrades}
            grades={grades}
            setGrades={setGrades}
            handleNext={handleGoForward}
          />
        )}
        {currentForm === 'intendedUses' && (
          <IntendedUseSelection
            activeFormSection={activeFormSectionMapper[currentForm]}
            customIntendedUse={customIntendedUse}
            setCustomIntendedUse={setCustomIntendedUse}
            intendedUses={intendedUses}
            setIntendedUse={setIntendedUse}
            handleNext={handleGoForward}
          />
        )}
        {currentForm === 'signUpOrigin' && (
          <SignUpOriginSelection
            activeFormSection={activeFormSectionMapper[currentForm]}
            customSignUpOrigin={customSignUpOrigin}
            setCustomSignUpOrigin={setCustomSignUpOrigin}
            signUpOrigin={signUpOrigin}
            setSignUpOrigin={setSignUpOrigin}
            handleNext={handleGoForward}
          />
        )}
        {currentForm === 'referralForm' &&
          referralStepEnabled &&
          selectedOrganization?.organization.country === 'US' && (
            <ReferralForm handleNext={handleGoForward} />
          )}
        {currentForm === 'studentJoinForm' && <StudentJoinLesson />}
        {currentForm !== 'selectOrganization' &&
          currentForm !== 'studentJoinForm' &&
          currentForm !== 'referralForm' &&
          activeFormSectionMapper[currentForm] &&
          activeFormSectionMapper[currentForm].canMultiSelect && (
            <Button
              type="ghost"
              styles={{
                border: 'none',
                color: theme.colors.primary,
                background: theme.colors.white,
                padding: 0,
                boxShadow: 'none',
              }}
              onClick={allOptionsSelected ? handleDeselectAll : handleSelectAll}
            >
              {allOptionsSelected ? 'Deselect All' : 'Select All'}
            </Button>
          )}

        <span style={{ fontWeight: 500 }}>Created with 🧡 from 🇳🇴</span>
      </SignupFlowContainer>
    </>
  );
};

const SignupFlowContainer = styled.div`
  width: 100%;
  background-color: ${theme.colors.white};
  display: flex;
  gap: 30px;
  flex-direction: column;
  align-items: center;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  border-radius: 15px;
  padding: 20px 20px 30px 20px;
`;

const OptionsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 15px;
  align-items: center;
  justify-content: center;
`;

const StyledOption = styled.div<{ $isSelected: boolean }>`
  padding: 10px 20px;
  border-radius: 30px;
  background-color: ${theme.colors.white};
  color: ${theme.colors.black};
  font-weight: 600;
  font-size: 16px;
  text-align: center;
  border: 1px solid ${theme.colors.black};
  cursor: pointer;
  transition: 0.2s ease-in-out;

  &:hover {
    background-color: ${theme.colors.grey};
    color: ${({ $isSelected }) =>
      $isSelected ? theme.colors.green : theme.colors.black};
    border: 1px solid ${theme.colors.white};
  }

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      color: ${theme.colors.green};
      border: 1px solid ${theme.colors.green};
    `}
`;

const Login = () => {
  const metrics = useMetrics(true);
  const { signIn } = useSignIn();
  const router = useRouter();
  const { t } = useTranslation('Host');
  const { track } = useMarketingMetrics();
  const callback = router.query.callback as string | undefined;
  const isLargerThan500 = useBreakPoint(500);
  const handleLogin = () => {
    signIn({
      callbackUrl: callback ? decodeURIComponent(callback) : '/',
    });
    track('SignUp.Topbar.Login');
    metrics.logEvent('SignUp.Topbar.Login');
  };
  return (
    <LoginText onClick={handleLogin}>
      {isLargerThan500 ? t('Already have an account? Log in') : t('Log in')}
    </LoginText>
  );
};

const LoginText = styled.span`
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const InfoBar = ({
  handleGoBack,
  canGoBack,
  canSkipCurrentForm,
  handleGoForward,
  showProgress,
  formNumber,
}: {
  handleGoBack: () => void;
  canGoBack: boolean;
  canSkipCurrentForm: boolean;
  handleGoForward: (isSkip?: boolean) => void;
  showProgress: boolean;
  formNumber: number;
}) => {
  const { t } = useTranslation('Host');
  const isLargerThan500 = useBreakPoint(500);
  return (
    <InfoBarContainer>
      {canGoBack && (
        <Button
          styles={{
            border: 'none',
            position: 'absolute',
            left: 10,
            top: 10,
            background: 'none',
            boxShadow: 'none',
          }}
          icon={<ChevronLeft color={theme.colors.black} />}
          onClick={() => {
            handleGoBack();
          }}
          type="ghost"
        >
          {isLargerThan500 ? t('Go back') : ' '}
        </Button>
      )}
      <ProgressBarContainer $showProgress={showProgress}>
        <h2 style={{ gridColumn: '1 / -1', justifySelf: 'center', marginBottom: 0 }}>
          Create Account
        </h2>
        {showProgress && (
          <>
            <ProgressBar $isComplete={formNumber >= 0} />
            <ProgressBar $isComplete={formNumber >= 1} />
            <ProgressBar $isComplete={formNumber >= 2} />
            <ProgressBar $isComplete={formNumber >= 3} />
            <ProgressBar $isComplete={formNumber >= 4} />
            <ProgressBar $isComplete={formNumber >= 5} />
            <ProgressBar $isComplete={formNumber >= 6} />
          </>
        )}
      </ProgressBarContainer>
      {canSkipCurrentForm && (
        <Button
          styles={{
            border: 'none',
            position: 'absolute',
            right: 10,
            top: 10,
            flexDirection: 'row-reverse',
            background: 'none',
            boxShadow: 'none',
          }}
          icon={<ChevronRight color={theme.colors.black} />}
          onClick={() => handleGoForward(true)}
          type="ghost"
        >
          {isLargerThan500 ? t('Skip') : ' '}
        </Button>
      )}
    </InfoBarContainer>
  );
};

const InfoBarContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProgressBarContainer = styled.div<{ $showProgress: boolean }>`
  width: 280px;
  display: grid;
  grid-template-rows: ${({ $showProgress }) => ($showProgress ? '3fr 2fr' : '1fr')};
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 5px;
  margin: ${({ $showProgress }) => ($showProgress ? '0px' : '20px 0px')};
  @media (max-width: 500px) {
    width: 200px;
  }
`;
const ProgressBar = styled.div<{ $isComplete: boolean }>`
  width: 100%;
  height: 5px;
  border-radius: 5px;
  background: ${theme.colors.orange} !important;
  opacity: ${({ $isComplete }) => ($isComplete ? 1 : 0.3)};
`;

const InvalidEmailWarning = styled.div`
  position: absolute;
  top: -17px;
  right: 0px;
  background-color: ${theme.colors.red || '#ff4d4f'};
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    right: 12px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid ${theme.colors.red || '#ff4d4f'};
  }
`;

const Signup = () => {
  const { track } = useMarketingMetrics();
  const metrics = useMetrics(true);
  const [
    selectedOrganization,
    setSelectedOrganization,
  ] = useState<SelectedOrganizationType | null>(null);

  useEffect(() => {
    track('Signup.Started');
    metrics.logEvent('Signup.Started', undefined, true);
  }, [track, metrics]);

  return (
    <SignupPageContainer>
      <SignupTopbar />
      <PageContentContainer hasTopBar={true} style={{ padding: 0 }}>
        <Container>
          <CreateAccountForm onOrganizationSelect={setSelectedOrganization} />
          <HotjarSnippet selectedOrganization={selectedOrganization} />
        </Container>
      </PageContentContainer>
    </SignupPageContainer>
  );
};

const SignupPageContainer = styled.div`
  height: 100%;
  width: 100%;

  @media (max-width: 550px) {
    h1 {
      font-size: 25px;
      margin-bottom: 0px;
    }
  }
`;
const Container = styled.div`
  height: 100%;
  display: flex;
  max-width: 1000px;
  margin: 0px auto;
  padding: 0px 20px 40px 20px;
  align-items: center;
  flex-direction: column;
  position: relative;
`;

export default Signup;
